import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { TechniquesStep } from './steps/TechniquesStep';
import { InterestsStep } from './steps/HobbiesStep';
import { SocialStep } from './steps/SocialStep';
import { ProfessionWizardStep } from './steps/ProfessionWizardStep';
import { InstructorsStep } from './steps/InstructorsStep';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { WizardStepContainer } from './steps/WizardStepContainer';
import { useI18n } from '@/contexts/I18nContext';

// Steps mockados (exceto Techniques e Interests, que agora são reais)
const steps = [
  'Profession',
  'Techniques',
  'Interests',
  'Instructors',
  'Social',
];

type ProfilePersonalizationWizardProps = {
  onComplete: (data: any) => void;
  profile: any;
  setProfile: React.Dispatch<React.SetStateAction<any>>;
};

// Mapeamento de nomes amigáveis para nomes do banco
const techniqueNameMap: Record<string, string> = {
  'Closed Guard': 'Closed Guard',
  'Open Guard': 'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)',
  'Half Guard': 'Half Guard (e.g., Deep Half, Z-Guard, Knee Shield)',
  'Butterfly Guard': 'Butterfly Guard',
  'Worm Guard': 'Worm Guard',
  'Reverse De La Riva Guard': 'Reverse De La Riva Guard (RDLR)',
  '50/50 Guard': '50/50 Guard',
  'Sitting Guard': 'Sitting Guard',
  'No-Gi Specific Guards': 'No-Gi Specific Guards (e.g., Leg Pummeling, Ashi Garami)',
  'Other Guard': 'Other Guard',

  'Pressure Passing': 'Pressure Passing (e.g., Knee Slice, Over Under, Stack Pass)',
  'Torque/Leg Drag Passing': 'Torque/Leg Drag Passing',
  'Leg Weave Passing': 'Leg Weave Passing',
  'Floating/Smash Passing': 'Floating/Smash Passing',
  'Movement-Based Passing': 'Movement-Based Passing (e.g., Toreando, Matador)',
  'Folding Passes': 'Folding Passes (e.g., Double Under)',
  'No-Gi Specific Passes': 'No-Gi Specific Passes (e.g., Head & Arm Pass, Darce Pass from Guard)',
  'Other Guard Pass': 'Other Guard Pass',

  'Armlocks': 'Armlocks (e.g., Armbar, Kimura, Americana, Omoplata)',
  'Chokes': 'Chokes (e.g., Rear Naked Choke, Guillotine, Triangle, Cross Collar Choke, Darce, Anaconda, Bow and Arrow)',
  'Leg Locks': 'Leg Locks (e.g., Heel Hook, Kneebar, Ankle Lock, Calf Slicer)',
  'Shoulder Locks': 'Shoulder Locks (e.g., Wrist Lock, Omoplata, Americana)',
  'Spine/Neck Cranks': 'Spine/Neck Cranks (e.g., Twister)',
  'No-Gi Specific Submissions': 'No-Gi Specific Submissions (e.g., Head & Arm Choke, Ezekiel from Mount)',
  'Other Submission': 'Other Submission',

  'Guard Sweeps': 'Guard Sweeps (e.g., Scissor Sweep, Hip Bump Sweep, Flower Sweep, Hook Sweeps)',
  'Half Guard Sweeps': 'Half Guard Sweeps (e.g., Old School Sweep, Waiter Sweep)',
  'Butterfly Sweeps': 'Butterfly Sweeps',
  'De La Riva Sweeps': 'De La Riva Sweeps',
  'Takedowns from Standing': 'Takedowns from Standing (e.g., Double Leg, Single Leg, Judo Throws, Body Lock)',
  'Counter Takedowns': 'Counter Takedowns',
  'Pulling Guard': 'Pulling Guard',
  'Other Sweep / Takedown': 'Other Sweep / Takedown',

  'Mount Escapes': 'Mount Escapes (e.g., Upa, Elbow-Knee Escape)',
  'Back Escapes': 'Back Escapes (e.g., Bridge & Roll, Hip Escape)',
  'Side Control Escapes': 'Side Control Escapes (e.g., Hip Escape to Guard, Underhook Escapes)',
  'North South Escapes': 'North South Escapes',
  'Submission Escapes': 'Submission Escapes (e.g., Armbar Escape, Triangle Escape)',
  'Turtle Escapes': 'Turtle Escapes',
  'Half Guard Escapes': 'Half Guard Escapes',
  'Other Escape / Reversal': 'Other Escape / Reversal',
};

export function ProfilePersonalizationWizard({ onComplete, profile, setProfile }: ProfilePersonalizationWizardProps) {
  const [step, setStep] = useState(-1); // -1 = introdução
  const [selectedTechniques, setSelectedTechniques] = useState<Record<string, string[]>>({});
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [socialFields, setSocialFields] = useState({
    instagram: '',
    x: '',
    facebook: '',
    youtube: '',
    website: '',
  });
  const [schoolCompanyFields, setSchoolCompanyFields] = useState({
    jiu_jitsu_school: '',
    company: '',
  });
  const [profession, setProfession] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [linkedinProfile, setLinkedinProfile] = useState('');
  const [instructors, setInstructors] = useState<Array<{
    id: string;
    full_name: string;
    username: string;
    profile_picture_url?: string;
    belt_rank?: string;
  }>>([]);
  const [skipped, setSkipped] = useState(false);

  const updateProfile = useUpdateProfile();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useI18n();

  // Limpar todos os estados sempre que o wizard for mostrado
  useEffect(() => {
    setSelectedTechniques({});
    setSelectedInterests([]);
    setSocialFields({
      instagram: '',
      x: '',
      facebook: '',
      youtube: '',
      website: '',
    });
    setSchoolCompanyFields({
      jiu_jitsu_school: '',
      company: '',
    });
    setProfession('');
    setBusinessType('');
    setLinkedinProfile('');
    setSkipped(false);
    setStep(-1);
  }, [profile?.id]); // Limpar sempre que o perfil mudar (wizard sendo mostrado)

  // Função para salvar técnicas favoritas
  const saveTechniques = async () => {
    if (!user || Object.keys(selectedTechniques).length === 0) {
      return;
    }

    try {
      // Primeiro, deletar todas as técnicas existentes do usuário
      const { error: deleteError } = await supabase
        .from('profile_favorite_techniques')
        .delete()
        .eq('profile_id', user.id);

      if (deleteError) {
        throw deleteError;
      }

      // Normalizar nomes antes de buscar no banco
      const normalizedNames = Object.values(selectedTechniques)
        .flat()
        .map(name => techniqueNameMap[name] || name);

      const techniquesToInsert = normalizedNames.map(techniqueName => ({ technique_name: techniqueName }));

      if (techniquesToInsert.length > 0) {
        // Buscar os IDs das técnicas pelo nome
        const { data: techniquesData, error: techniquesError } = await supabase
          .from('techniques')
          .select('id, name')
          .in('name', normalizedNames);

        if (techniquesError) {
          throw techniquesError;
        }

        if (techniquesData && techniquesData.length > 0) {
          // Inserir as relações
          const relationsToInsert = techniquesData.map(tech => ({
            profile_id: user.id,
            technique_id: tech.id
          }));

          const { error: insertError } = await supabase
            .from('profile_favorite_techniques')
            .insert(relationsToInsert);

          if (insertError) {
            throw insertError;
          }
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Função para salvar interests
  const saveInterests = async () => {
    if (!user || selectedInterests.length === 0) {
      return;
    }

    try {
      // Primeiro, deletar todos os interests existentes do usuário
      const { error: deleteError } = await supabase
        .from('profile_hobbies')
        .delete()
        .eq('profile_id', user.id);

      if (deleteError) {
        throw deleteError;
      }

      // Depois, inserir os novos interests selecionados
      const { data: interestsData, error: interestsError } = await supabase
        .from('hobbies')
        .select('id, name')
        .in('name', selectedInterests);

      if (interestsError) {
        throw interestsError;
      }

      if (interestsData && interestsData.length > 0) {
        const relationsToInsert = interestsData.map(interest => ({
          profile_id: user.id,
          hobby_id: interest.id
        }));

        const { error: insertError } = await supabase
          .from('profile_hobbies')
          .insert(relationsToInsert);

        if (insertError) {
          throw insertError;
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Função para salvar instrutores
  const saveInstructors = async () => {
    if (!user || instructors.length === 0) {
      return;
    }

    try {
      // Save each instructor to the profile
      for (const instructor of instructors) {
        if (instructor.username === 'manual') {
          // Manual instructor
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add_profile_instructor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              profile_id: user.id,
              manual_name: instructor.full_name
            })
          });
        } else {
          // sOSSiety user instructor
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add_profile_instructor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              profile_id: user.id,
              instructor_user_id: instructor.id
            })
          });
        }
      }
    } catch (error) {
      throw error;
    }
  };

  // Função para salvar academia
  const saveAcademy = async () => {
    if (!user || !schoolCompanyFields.jiu_jitsu_school) {
      return;
    }

    try {
      // Buscar a academia pelo nome
      const { data: academyData, error: academyError } = await supabase
        .from('academies')
        .select('id, name')
        .eq('name', schoolCompanyFields.jiu_jitsu_school)
        .single();

      if (academyError && academyError.code !== 'PGRST116') {
        throw academyError;
      }

      if (academyData) {
      } else {
      }

      // Atualizar o main_academy_id no perfil
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ main_academy_id: academyData?.id || null })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }
    } catch (error) {
      throw error;
    }
  };

  // Função para coletar apenas os dados preenchidos até o momento
  const collectFilledData = () => {
    const data: any = {};
    if (Object.values(selectedTechniques).flat().length > 0) {
      data.techniques = selectedTechniques;
    }
    if (selectedInterests.length > 0) {
      data.interests = selectedInterests;
    }
    if (Object.values(socialFields).some(v => v)) {
      data.social = socialFields;
    }
    if (schoolCompanyFields.jiu_jitsu_school || schoolCompanyFields.company) {
      data.schoolCompany = schoolCompanyFields;
    }
    if (profession) {
      data.profession = profession;
    }
    if (linkedinProfile) {
      data.linkedinProfile = linkedinProfile;
    }
    return data;
  };

  const handleNext = async () => {
    // Apenas avança para o próximo passo, não marca como completed
    setSkipped(false);
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const saveProfileData = async () => {
    try {
      const profileData: any = {};

      // Add profession data
      if (profession) {
        profileData.profession = profession;
      }

      // Add business type data: só envia se for entrepreneur, senão envia string vazia
      if (profession === 'Entrepreneur / Business Owner') {
        profileData.business_type = businessType || '';
      } else {
        profileData.business_type = '';
      }

      // Add social data
      if (Object.values(socialFields).some(v => v)) {
        Object.assign(profileData, socialFields);
      }

      // Add school/company data
      if (schoolCompanyFields.jiu_jitsu_school) {
        profileData.jiu_jitsu_school = schoolCompanyFields.jiu_jitsu_school;
      }
      if (schoolCompanyFields.company) {
        profileData.company = schoolCompanyFields.company;
      }

      // Add LinkedIn data
      if (linkedinProfile) {
        profileData.linkedin = linkedinProfile;
      }

      // Add is_instructor field (pegar do perfil atual)
      if (profile && profile.is_instructor !== undefined) {
        profileData.is_instructor = profile.is_instructor;
      }

      // Update profile if there's data to save
      if (Object.keys(profileData).length > 0) {
        await updateProfile.mutateAsync(profileData);
      }

      // Save techniques, interests, instructors, and academy
      await saveTechniques();
      await saveInterests();
      await saveInstructors();
      await saveAcademy();

    } catch (error) {
      toast({
        title: t('onboarding.messages.error_saving_profile'),
        description: t('onboarding.messages.error_saving_profile_description'),
        variant: "destructive"
      });
      
      // Re-throw the error so it can be handled by the calling function
      throw error;
    }
  };

  const handleSkip = async () => {
    try {
      setSkipped(true);
      await saveProfileData();
      toast({
        title: t('onboarding.messages.profile_complete_later'),
        description: t('onboarding.messages.profile_complete_later_description')
      });
      onComplete(false); // skip: não preencheu nada
    } catch (error) {
    }
  };

  const handleComplete = async () => {
    try {
      setSkipped(false);
      await saveProfileData();
      
      // Marcar optional_fields_completed como true apenas quando completa o wizard
      await updateProfile.mutateAsync({ optional_fields_completed: true });
      
      // Update local profile state
      setProfile((prev) => ({
        ...prev,
        optional_fields_completed: true,
      }));
      
      toast({
        title: t('onboarding.messages.profile_updated_successfully'),
        description: t('onboarding.messages.profile_updated_description')
      });
      onComplete(true); // completou o wizard
    } catch (error) {
    }
  };

  // Função para verificar se os dados foram salvos corretamente
  const verifySavedData = async () => {
    if (!user) return;

    try {
      // Verificar perfil
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        return;
      }

      // Verificar técnicas
      const { data: techniquesData, error: techniquesError } = await supabase
        .from('profile_favorite_techniques')
        .select(`
          technique_id,
          techniques (name)
        `)
        .eq('profile_id', user.id);

      if (techniquesError) {
      } else {
      }

      // Verificar interests
      const { data: interestsData, error: interestsError } = await supabase
        .from('profile_hobbies')
        .select(`
          hobby_id,
          hobbies (name)
        `)
        .eq('profile_id', user.id);

      if (interestsError) {
      } else {
      }

      // Verificar academia
      if (profileData.main_academy_id) {
        const { data: academyData, error: academyError } = await supabase
          .from('academies')
          .select('name')
          .eq('id', profileData.main_academy_id)
          .single();

        if (academyError) {
        } else {
        }
      }

    } catch (error) {
    }
  };

  // Função para saber se está no primeiro slide de Techniques
  const isFirstStep = step === 0;

  // Função para saber se está no último passo
  const isLastStep = step === steps.length - 1;

  // Progresso geral do wizard
  const totalSteps = steps.length;
  const currentStep = step;

  // Técnicas: controle interno de categoria
  const [techniqueCategoryIndex, setTechniqueCategoryIndex] = useState(0);
  const resetTechniquesStep = () => setTechniqueCategoryIndex(0);

  // Renderização dos passos usando WizardStepContainer
  if (step === 0) {
    return (
      <WizardStepContainer
        step={currentStep}
        totalSteps={totalSteps}
        onSkip={handleSkip}
        onNext={handleNext}
        onComplete={handleComplete}
        isLastStep={false}
      >
        <ProfessionWizardStep
          selected={profession}
          setSelected={setProfession}
          businessType={businessType}
          setBusinessType={setBusinessType}
          linkedinProfile={linkedinProfile}
          setLinkedinProfile={setLinkedinProfile}
        />
      </WizardStepContainer>
    );
  }
  if (step === 1) {
    return (
      <WizardStepContainer
        step={currentStep}
        totalSteps={totalSteps}
        onSkip={handleSkip}
        onNext={() => {
          // Se não for a última categoria, avança categoria interna
          if (techniqueCategoryIndex < 4) {
            setTechniqueCategoryIndex((i) => i + 1);
          } else {
            // Última categoria: avança para o próximo passo do wizard
            setStep(step + 1);
            resetTechniquesStep();
          }
        }}
        onComplete={handleComplete}
        isLastStep={false}
        nextLabel={techniqueCategoryIndex < 4 ? t('onboarding.next') : t('onboarding.next')}
        completeLabel={t('onboarding.next')}
      >
        <TechniquesStep
          categoryIndex={techniqueCategoryIndex}
          setCategoryIndex={setTechniqueCategoryIndex}
          selected={selectedTechniques}
          setSelected={setSelectedTechniques}
        />
      </WizardStepContainer>
    );
  }
  if (step === 2) {
    return (
      <WizardStepContainer
        step={currentStep}
        totalSteps={totalSteps}
        onSkip={handleSkip}
        onNext={handleNext}
        onComplete={handleComplete}
        isLastStep={false}
      >
        <InterestsStep
          selected={selectedInterests}
          setSelected={setSelectedInterests}
        />
      </WizardStepContainer>
    );
  }
  if (step === 3) {
    return (
      <InstructorsStep
        onNext={handleNext}
        onBack={() => setStep(step - 1)}
        onComplete={handleComplete}
        currentStep={currentStep}
        totalSteps={totalSteps}
        instructors={instructors}
        setInstructors={setInstructors}
      />
    );
  }
  if (step === 4) {
    return (
      <WizardStepContainer
        step={currentStep}
        totalSteps={totalSteps}
        onSkip={handleSkip}
        onNext={handleNext}
        onComplete={handleComplete}
        isLastStep={true}
        completeLabel={t('onboarding.finish')}
      >
        <SocialStep
          selected={socialFields}
          setSelected={setSocialFields}
        />
      </WizardStepContainer>
    );
  }
  // Introdução
  if (step === -1) {
    return (
      <div className="max-w-lg mx-auto p-8 bg-white rounded-xl shadow flex flex-col items-center text-center">
        <h2 className="text-2xl font-bold mb-4 font-sans text-black">{t('onboarding.welcome')}</h2>
        <p className="mb-6 text-[#b0b0b0] font-sans">
          {t('onboarding.welcome_subtitle')}
        </p>
        <div className="flex flex-col md:flex-row gap-4 w-full justify-center">
          <Button
            className="bg-[#D72E16] text-white px-6 py-3 rounded-full text-lg font-semibold w-full md:w-auto font-sans"
            onClick={() => setStep(0)}
          >
            {t('onboarding.next')}
          </Button>
          <Button
            variant="outline"
            className="border-black text-black px-6 py-3 rounded-full text-lg font-semibold bg-white w-full md:w-auto font-sans"
            onClick={handleSkip}
          >
            {t('onboarding.skip')}
          </Button>
        </div>
      </div>
    );
  }
  return null;
} 